import { Close as CloseIcon } from "@mui/icons-material";
import clsx from "clsx";
import { useCallback, useMemo, useState, type FC, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  CourseRouteTypeEnum,
  DATE_DD_MM_FORMAT,
  DirectionEnum,
  formatDate,
  formatHHmmTime,
  NO_VALUE_DISPLAY,
  onActivateKeyDown,
  resolveCourseScheduleExistingRideStatusText,
  resolveCourseScheduleExistingStatusLabel,
  RideStatusEnum,
  TimeZoneEnum,
  useDrawers,
  useFormats,
  useHaveCustomPermissions,
  useTimezone,
  type IProductionCourseSchedulePreview,
} from "@ryde/shared";

import { CheckboxMui } from "../../../controls";
import { CourseIndicatorIcon } from "../../../grid/cell-templates/rides/summary/course.indicator.icon";
import { FramerTooltip } from "../../tooltips/framer-tooltip";
import { BookingPassenger, BookingStation } from "./parts";
import {
  CourseScheduleAccordionButton,
  CourseScheduleTooltipContent,
  CourseScheduleWrapper,
} from "./production-course-schedule-preview.styled";

interface IProductionCourseSchedulePreviewProps {
  courseSchedulePreviews?: IProductionCourseSchedulePreview[];
}

export const ProductionCourseSchedulePreview: FC<
  IProductionCourseSchedulePreviewProps
> = ({ courseSchedulePreviews }) => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { timeFormat } = useFormats();
  const {
    openCourseEditFormDrawer,
    openBookingEditFormDrawer,
    openEditFormDrawer,
    openRideEditFormDrawer,
  } = useDrawers();
  const {
    canOpenCourseDrawer,
    canViewBooking,
    canEditBooking,
    canEditStation,
    canOpenPassengerCard,
    hasPermissionEditRide,
    hasPermissionViewRide,
  } = useHaveCustomPermissions();
  const canOpenRideDrawer = hasPermissionEditRide || hasPermissionViewRide;
  const [isExpanded, setIsExpanded] = useState(true);
  const [forProduceOnly, setForProduceOnly] = useState(false);
  const [expandedCourseRows, setExpandedCourseRows] = useState<Set<string>>(
    new Set(),
  );

  const sortedSchedules = useMemo(() => {
    if (!courseSchedulePreviews?.length) {
      return [];
    }

    // Will-create first, then already-exist (reason visible in status column).
    return [...courseSchedulePreviews].sort((left, right) => {
      const leftExisting = left.existingRideId ? 1 : 0;
      const rightExisting = right.existingRideId ? 1 : 0;
      return leftExisting - rightExisting;
    });
  }, [courseSchedulePreviews]);

  const willCreateSchedules = useMemo(
    () => sortedSchedules.filter(schedule => !schedule.existingRideId),
    [sortedSchedules],
  );
  const alreadyExistSchedules = useMemo(
    () => sortedSchedules.filter(schedule => Boolean(schedule.existingRideId)),
    [sortedSchedules],
  );

  const visibleSchedules = useMemo(() => {
    if (!forProduceOnly) {
      return sortedSchedules;
    }
    return willCreateSchedules;
  }, [forProduceOnly, sortedSchedules, willCreateSchedules]);

  const resolveDirectionLabel = useCallback(
    (direction: DirectionEnum): string => {
      switch (direction) {
        case DirectionEnum.PickUp:
          return t("selectListLabel.DirectionA");
        case DirectionEnum.DropOff:
          return t("selectListLabel.DirectionB");
        default:
          return direction;
      }
    },
    [t],
  );

  const resolveDirectionClassName = useCallback(
    (direction: DirectionEnum): string => {
      switch (direction) {
        case DirectionEnum.PickUp:
          return "course-schedule__direction course-schedule__direction--a";
        case DirectionEnum.DropOff:
          return "course-schedule__direction course-schedule__direction--b";
        default:
          return "course-schedule__direction";
      }
    },
    [],
  );

  const toggleForProduceOnly = useEvent(() => {
    setForProduceOnly(prev => !prev);
  });

  const handleCourseClick = useEvent((courseId: number) => {
    if (canOpenCourseDrawer && courseId) {
      openCourseEditFormDrawer({ id: courseId });
    }
  });

  const handleRideClick = useEvent((rideId: number) => {
    if (canOpenRideDrawer && rideId) {
      void openRideEditFormDrawer({ id: rideId });
    }
  });

  const resolveExistingStatusLabel = useCallback(
    (schedule: IProductionCourseSchedulePreview): string =>
      resolveCourseScheduleExistingStatusLabel({
        t,
        existingRideId: schedule.existingRideId,
        existingRideStatus: schedule.existingRideStatus,
        forNewSchedule: true,
      }),
    [t],
  );

  const resolveExistingRideStatusText = useCallback(
    (schedule: IProductionCourseSchedulePreview): string | undefined =>
      resolveCourseScheduleExistingRideStatusText({
        t,
        existingRideStatus: schedule.existingRideStatus,
      }),
    [t],
  );

  const resolveExistingStatusTooltip = useCallback(
    (schedule: IProductionCourseSchedulePreview): ReactNode => {
      const label = resolveExistingStatusLabel(schedule);
      if (!schedule.existingRideId) {
        return label;
      }

      // Schedule times are course wall-clock HH:mm, so no timezone shift.
      const scheduleTime = formatHHmmTime(
        schedule.time,
        timeFormat,
        TimeZoneEnum.UTC,
      );
      const datetimeLabel = schedule.rideDate
        ? `${formatDate({
            dateISO: schedule.rideDate,
            dateFormat: "DD.MM",
            timezone,
          })} ${scheduleTime}`
        : scheduleTime;
      const rideStatusText = resolveExistingRideStatusText(schedule);
      const rideIdLabel = rideStatusText
        ? `#${schedule.existingRideId} (${rideStatusText})`
        : `#${schedule.existingRideId}`;

      return (
        <CourseScheduleTooltipContent>
          <div>
            {t("rideProduction.courseScheduleAlreadyCreatedAt", {
              datetime: datetimeLabel,
            })}
          </div>
          <div>{rideIdLabel}</div>
        </CourseScheduleTooltipContent>
      );
    },
    [
      resolveExistingRideStatusText,
      resolveExistingStatusLabel,
      t,
      timeFormat,
      timezone,
    ],
  );

  const handleBookingClick = useEvent((bookingId: number) => {
    if (bookingId && canEditBooking) {
      openBookingEditFormDrawer({ id: bookingId });
    }
  });

  const handleStationClick = useEvent((stationId: number) => {
    if (stationId && canEditStation) {
      openEditFormDrawer({ entity: "station", id: stationId });
    }
  });

  const handlePassengerClick = useEvent((passengerId: number) => {
    if (passengerId && canOpenPassengerCard) {
      openEditFormDrawer({ entity: "passenger", id: passengerId });
    }
  });

  const toggleCourseRowExpanded = useEvent((rowKey: string) => {
    setExpandedCourseRows(prev => {
      const next = new Set(prev);
      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
      }
      return next;
    });
  });

  if (!courseSchedulePreviews?.length) {
    return null;
  }

  return (
    <>
      <CourseScheduleAccordionButton
        type="button"
        onClick={() => setIsExpanded(prev => !prev)}
        $isOpen={isExpanded}
      >
        <span>{t("rideProduction.courseSchedulePreviewTitle")}</span>
        <span className="course-schedule__badges">
          <span className="course-schedule__badge course-schedule__badge--ready">
            {t("rideProduction.courseScheduleForProduceBadge", {
              count: willCreateSchedules.length,
            })}
          </span>
          {alreadyExistSchedules.length > 0 ? (
            <span className="course-schedule__badge course-schedule__badge--skipped">
              {t("rideProduction.courseScheduleSkippedBadge", {
                count: alreadyExistSchedules.length,
              })}
            </span>
          ) : null}
          <span className="course-schedule__badge course-schedule__badge--total">
            {t("rideProduction.courseScheduleTotalBadge", {
              count: sortedSchedules.length,
            })}
          </span>
        </span>
        <span className="course-schedule__chevron" aria-hidden />
      </CourseScheduleAccordionButton>
      {isExpanded && (
        <CourseScheduleWrapper>
          <label className="course-schedule__filter">
            <CheckboxMui
              width={15}
              height={15}
              checked={forProduceOnly}
              onCheckboxToggle={toggleForProduceOnly}
            />
            <span>
              {`${t("rideProduction.courseScheduleForProduceOnly")} (${willCreateSchedules.length})`}
            </span>
          </label>
          <div className="course-schedule__row course-schedule__row--header">
            <span>{t("rideProduction.courseScheduleTime")}</span>
            <span>{t("course.formLabel.routeType")}</span>
            <span>{t("rideProduction.courseScheduleCourse")}</span>
            <span className="course-schedule__passengers">
              {t("rideProduction.courseScheduleBookings")}
            </span>
            <span className="course-schedule__status">
              {t("rideProduction.courseScheduleStatus")}
            </span>
          </div>
          {visibleSchedules.map(schedule => {
            const rowKey = `${schedule.courseId}-${schedule.time}-${schedule.direction}`;
            const isRowExpanded = expandedCourseRows.has(rowKey);
            const isSkippedExisting = Boolean(schedule.existingRideId);
            const existingRideStatusText =
              resolveExistingRideStatusText(schedule);
            const hasBookings =
              (schedule.bookings && schedule.bookings.length > 0) ||
              (schedule.bookingIds && schedule.bookingIds.length > 0);
            const bookingsCount =
              schedule.bookings?.length ?? schedule.bookingIds?.length ?? 0;

            return (
              <div key={rowKey}>
                <div
                  className={clsx("course-schedule__row", {
                    ["course-schedule__row--expandable"]: hasBookings,
                    ["course-schedule__row--skipped"]: isSkippedExisting,
                  })}
                  onClick={
                    hasBookings
                      ? () => toggleCourseRowExpanded(rowKey)
                      : undefined
                  }
                  onKeyDown={
                    hasBookings
                      ? onActivateKeyDown(() => toggleCourseRowExpanded(rowKey))
                      : undefined
                  }
                  role={hasBookings ? "button" : undefined}
                  tabIndex={hasBookings ? 0 : undefined}
                >
                  <span>
                    {schedule.rideDate
                      ? `${formatDate({ dateISO: schedule.rideDate, dateFormat: "DD.MM", timezone })} ${formatHHmmTime(schedule.time, timeFormat, TimeZoneEnum.UTC)}`
                      : formatHHmmTime(
                          schedule.time,
                          timeFormat,
                          TimeZoneEnum.UTC,
                        )}{" "}
                    ·{" "}
                    <span
                      className={resolveDirectionClassName(schedule.direction)}
                    >
                      {resolveDirectionLabel(schedule.direction)}
                    </span>
                  </span>
                  <span className="course-schedule__type">
                    <FramerTooltip
                      content={renderCourseTypeTooltip(schedule, t)}
                    >
                      <div className="course-schedule__type-content">
                        <CourseIndicatorIcon
                          routeType={schedule.courseRouteType}
                        />
                        <span>{getCourseTypeLabel(schedule, t)}</span>
                      </div>
                    </FramerTooltip>
                  </span>
                  <span
                    className="course-schedule__name"
                    title={getCourseDisplayName(schedule)}
                  >
                    {canOpenCourseDrawer && schedule.courseId ? (
                      <span
                        className="course-schedule__name-id course-schedule__name-id--clickable"
                        onClick={e => {
                          e.stopPropagation();
                          handleCourseClick(schedule.courseId!);
                        }}
                        onKeyDown={onActivateKeyDown(e => {
                          e.stopPropagation();
                          handleCourseClick(schedule.courseId!);
                        })}
                        role="button"
                        tabIndex={0}
                      >
                        #{schedule.courseId}
                      </span>
                    ) : (
                      <span className="course-schedule__name-id">
                        {schedule.courseId
                          ? `#${schedule.courseId}`
                          : NO_VALUE_DISPLAY}
                      </span>
                    )}
                    {schedule.courseName ? (
                      <span className="course-schedule__name-text">
                        {` ${schedule.courseName}`}
                      </span>
                    ) : null}
                  </span>
                  <span className="course-schedule__passengers">
                    {hasBookings && (
                      <button
                        type="button"
                        className="course-schedule__expand-button"
                        onClick={e => {
                          e.stopPropagation();
                          toggleCourseRowExpanded(rowKey);
                        }}
                        aria-label={
                          isRowExpanded
                            ? t("general.COLLAPSE")
                            : t("general.EXPAND")
                        }
                      >
                        <span
                          className={clsx("course-schedule__expand-icon", {
                            ["course-schedule__expand-icon--expanded"]:
                              isRowExpanded,
                          })}
                        />
                      </button>
                    )}
                    {bookingsCount}
                  </span>
                  <span className="course-schedule__status">
                    <span
                      className={clsx("course-schedule__status-cell", {
                        ["course-schedule__status-cell--existing"]:
                          isSkippedExisting,
                        ["course-schedule__status-cell--canceled"]:
                          schedule.existingRideStatus ===
                          RideStatusEnum.Canceled,
                      })}
                    >
                      <FramerTooltip
                        content={resolveExistingStatusTooltip(schedule)}
                      >
                        <span
                          className={clsx("course-schedule__status-icon", {
                            ["course-schedule__status-icon--existing"]:
                              isSkippedExisting,
                            ["course-schedule__status-icon--canceled"]:
                              schedule.existingRideStatus ===
                              RideStatusEnum.Canceled,
                          })}
                          aria-label={resolveExistingStatusLabel(schedule)}
                        >
                          {isSkippedExisting ? (
                            <CloseIcon fontSize="inherit" />
                          ) : (
                            "✓"
                          )}
                        </span>
                      </FramerTooltip>
                      {isSkippedExisting ? (
                        <span className="course-schedule__status-meta">
                          <span className="course-schedule__status-label">
                            {resolveExistingStatusLabel(schedule)}
                          </span>
                          <span className="course-schedule__ride-id-row">
                            {canOpenRideDrawer ? (
                              <span
                                className="course-schedule__ride-id course-schedule__ride-id--clickable"
                                onClick={e => {
                                  e.stopPropagation();
                                  handleRideClick(schedule.existingRideId!);
                                }}
                                onKeyDown={onActivateKeyDown(e => {
                                  e.stopPropagation();
                                  handleRideClick(schedule.existingRideId!);
                                })}
                                role="button"
                                tabIndex={0}
                              >
                                #{schedule.existingRideId}
                              </span>
                            ) : (
                              <span className="course-schedule__ride-id">
                                #{schedule.existingRideId}
                              </span>
                            )}
                            {existingRideStatusText ? (
                              <span className="course-schedule__ride-status">
                                {`(${existingRideStatusText})`}
                              </span>
                            ) : null}
                          </span>
                        </span>
                      ) : null}
                    </span>
                  </span>
                </div>
                {hasBookings &&
                  isRowExpanded &&
                  canViewBooking &&
                  schedule.bookings?.map(booking => (
                    <div
                      key={booking.id}
                      className="course-schedule__row course-schedule__row--booking"
                    >
                      {canEditBooking ? (
                        <span
                          className="course-schedule__booking-id course-schedule__booking-id--clickable"
                          onClick={() => handleBookingClick(booking.id)}
                          onKeyDown={onActivateKeyDown(() =>
                            handleBookingClick(booking.id),
                          )}
                          role="button"
                          tabIndex={0}
                        >
                          #{booking.id}
                        </span>
                      ) : (
                        <span className="course-schedule__booking-id">
                          #{booking.id}
                        </span>
                      )}
                      <span className="course-schedule__booking-time">
                        {booking.bookTime
                          ? formatDate({
                              dateISO: booking.bookTime,
                              dateFormat: `${DATE_DD_MM_FORMAT} ${timeFormat}`,
                              timezone,
                            })
                          : NO_VALUE_DISPLAY}
                      </span>
                      <span className="course-schedule__booking-stations">
                        <BookingStation
                          stationId={booking.originStationId}
                          stationName={booking.originStationName}
                          canEdit={canEditStation}
                          onStationClick={handleStationClick}
                        />
                        {" → "}
                        <BookingStation
                          stationId={booking.destinationStationId}
                          stationName={booking.destinationStationName}
                          canEdit={canEditStation}
                          onStationClick={handleStationClick}
                        />
                      </span>
                      <BookingPassenger
                        passengerId={booking.passengerId}
                        passengerFirstName={booking.passengerFirstName}
                        passengerLastName={booking.passengerLastName}
                        passengerWorkerCode={booking.passengerWorkerCode}
                        canOpenPassengerCard={canOpenPassengerCard}
                        onPassengerClick={handlePassengerClick}
                      />
                    </div>
                  ))}
              </div>
            );
          })}
          {!visibleSchedules.length && (
            <div className="course-schedule__empty">
              {t("general.NO_RESULTS")}
            </div>
          )}
        </CourseScheduleWrapper>
      )}
    </>
  );
};

const getCourseTypeLabel = (
  schedule: IProductionCourseSchedulePreview,
  t: (key: string) => string,
): string => {
  if (!schedule.courseRouteType) {
    return NO_VALUE_DISPLAY;
  }
  return t(`selectListLabel.${schedule.courseRouteType}`);
};

const getRebuildOptionLabel = (
  schedule: IProductionCourseSchedulePreview,
  t: (key: string) => string,
): string => {
  if (!schedule.rebuildRideOption) {
    return NO_VALUE_DISPLAY;
  }
  return t(`selectListLabel.${schedule.rebuildRideOption}`);
};

const getCourseDisplayName = (
  schedule: IProductionCourseSchedulePreview,
): string => {
  if (!schedule.courseId && !schedule.courseName) {
    return NO_VALUE_DISPLAY;
  }
  const parts = [`#${schedule.courseId ?? ""}`, schedule.courseName ?? ""];
  return parts.filter(Boolean).join(" ").trim();
};

const renderCourseTypeTooltip = (
  schedule: IProductionCourseSchedulePreview,
  t: (key: string) => string,
): ReactNode => {
  const typeLabel = getCourseTypeLabel(schedule, t);
  const rebuildLabel = getRebuildOptionLabel(schedule, t);
  const isFlex = schedule.courseRouteType === CourseRouteTypeEnum.Flex;
  return (
    <CourseScheduleTooltipContent>
      <div>
        {t("course.formLabel.routeType")}: {typeLabel}
      </div>
      {isFlex && schedule.rebuildRideOption && (
        <div>
          {t("course.formLabel.rebuildRideOption")}: {rebuildLabel}
        </div>
      )}
    </CourseScheduleTooltipContent>
  );
};
